import { useEffect, useState } from "react";
import { NavButtons } from "../components/NavButtons";
import { useSearchParams } from "react-router-dom";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { getCached, setCached } from "@/lib/cache";
import PageLoader from "@/components/PageLoader";
import { useTheme } from "@/context/ThemeContext";
import { useAuth } from "@/context/AuthContext";
import Tabs from "@/components/Tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { toast } from "sonner";
import { ArrowLeft, FloppyDisk, Plus, Trash, Sun, Moon, ArrowRight, X, PaperPlaneTilt } from "@phosphor-icons/react";

function RunDigestNowButton() {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/marketing-digest/run-now");
      toast.success(data.sent > 0 ? `Digest sent to ${data.sent} admin(s)` : "No pending changes older than 48h");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <button data-testid="run-mkt-digest-now" onClick={run} disabled={busy} className="btn-ghost text-xs">
      <PaperPlaneTilt size={12} weight="bold" /> {busy ? "Sending…" : "Run digest now"}
    </button>
  );
}

const EMAIL_GROUPS = [
  { key: "account", title: "Account & onboarding", emails: [
    { key: "welcome_email", title: "Welcome email", note: "Sent when an admin creates a client. 'Hi {name},' is auto-added.", vars: "{name} {email} {password}" },
    { key: "password_reset", title: "Password reset", vars: "{name} {email} {password}" },
    { key: "reminder_email", title: "Open tasks nudge (every 2 days)", vars: "{count} {name} {tasks}" },
  ]},
  { key: "tasks", title: "Tasks & notes", emails: [
    { key: "task_reminder_single", title: "Single task reminder", note: "Sent from the reminder button on a task.", vars: "{name} {task_title} {due_line} {due_date}" },
    { key: "task_note_client", title: "New note on a task", vars: "{name} {author} {task_title} {note}" },
    { key: "assigned_task_due", title: "Assigned task due / overdue", note: "Workboard tasks assigned to a client or admin.", vars: "{name} {task_title} {when} {due_date} {description}" },
  ]},
  { key: "payments", title: "Payments", emails: [
    { key: "payment_reminder", title: "Payment reminder", vars: "{name} {amount} {due_date}" },
  ]},
  { key: "project", title: "Project, samples & files", emails: [
    { key: "sample_email", title: "Sample delivered", note: "Leave the body empty to use the sample description.", vars: "{title} {description}" },
    { key: "sample_reminder", title: "Sample review reminder", vars: "{name} {title} {due_line} {due_date}" },
    { key: "sample_manual_approval", title: "Sample approved by PGD", vars: "{name} {title} {note}" },
    { key: "file_upload", title: "New file uploaded", vars: "{filename} {note} {note_line} {name}" },
    { key: "offboarding_kickoff", title: "Offboarding kickoff", vars: "{name} {tasks} {due_phrase}" },
  ]},
  { key: "messages", title: "Messages", emails: [
    { key: "handled_notice", title: "Request handled notice", vars: "{name} {subject}" },
  ]},
  { key: "marketing", title: "Marketing", emails: [
    { key: "marketing_update", title: "Marketing update posted", vars: "{name} {email} {update_type} {update_type_label} {week_of} {summary} {summary_block} {requested_change} {request_block}" },
    { key: "management_paused", title: "Management paused", note: "{reason_block} adds 'Reason: …' only when a reason is given.", vars: "{name} {reason} {reason_block}" },
    { key: "management_resumed", title: "Management resumed", vars: "{name}" },
  ]},
  { key: "internal", title: "Internal alerts (to PGD team)", emails: [
    { key: "task_marked_admin", title: "Task marked complete", vars: "{name} {email} {task_title} {status}" },
    { key: "support_email", title: "Support request", vars: "{subject} {body} {name} {email}" },
    { key: "marketing_digest", title: "Pending requested changes digest", note: "Emailed daily to admins for changes pending over 48 hours.", vars: "{count} {items}" },
  ]},
];

function EmailSettings() {
  const [settings, setSettings] = useState(() => getCached("admin-settings-email") ?? null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/admin/settings").then(r => {
      setSettings(r.data);
      setCached("admin-settings-email", r.data);
    });
  }, []);
  if (!settings) return <PageLoader label="Loading settings…" testid="admin-settings-loader" />;
  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/admin/settings", settings);
      toast.success("Email templates saved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };
  const field = (label, key, rows = 3, help = "") => (
    <div>
      <div className="flex items-baseline justify-between">
        <p className="label mb-1">{label}</p>
        {help && <p className="text-xs opacity-60">{help}</p>}
      </div>
      {rows === 1 ? (
        <input value={settings[key] || ""} onChange={(e) => setSettings({ ...settings, [key]: e.target.value })}
               data-testid={`setting-${key}`} className="w-full px-3 py-2" />
      ) : (
        <textarea value={settings[key] || ""} rows={rows}
               onChange={(e) => setSettings({ ...settings, [key]: e.target.value })}
               data-testid={`setting-${key}`} className="w-full px-3 py-2" />
      )}
    </div>
  );
  return (
    <div className="space-y-6" data-testid="email-settings">
      <Accordion type="multiple" className="space-y-3" data-testid="email-template-groups">
        {EMAIL_GROUPS.map((g) => (
          <AccordionItem key={g.key} value={g.key} className="panel border-0" data-testid={`email-group-${g.key}`}>
            <AccordionTrigger className="px-6 py-4 hover:no-underline" data-testid={`email-group-${g.key}-trigger`}>
              <span className="title-xl text-xl text-left">{g.title} <span className="label ml-2">({g.emails.length})</span></span>
            </AccordionTrigger>
            <AccordionContent className="px-6 pb-6 space-y-6">
              {g.emails.map((e) => (
                <div key={e.key} className="space-y-3 pt-4 border-t" style={{ borderColor: "var(--hair)" }}
                  data-testid={`email-tpl-${e.key}`}>
                  <div>
                    <h4 className="font-semibold">{e.title}</h4>
                    {e.note && <p className="text-xs mt-0.5" style={{ color: "var(--text-dim)" }}>{e.note}</p>}
                  </div>
                  {field("Subject", `${e.key}_subject`, 1, `Vars: ${e.vars}`)}
                  {field("Body", `${e.key}_body`, 5, `Vars: ${e.vars}`)}
                  {e.key === "marketing_digest" && <RunDigestNowButton />}
                </div>
              ))}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
      <div className="panel p-6 space-y-4">
        <h3 className="title-xl text-2xl">Payment methods</h3>
        {field("PayPal / CC / Debit link", "default_paypal_link", 1, "Applied to all clients")}
        {field("Zelle email", "zelle_email", 1, "e.g. PrettyGrlsDesign@Gmail.com")}
        {field("Zelle recipient name", "zelle_name", 1, "e.g. PrettyGrlsDesign")}
        {field("Cash App URL", "cashapp_url", 1, "e.g. https://cash.app/$prettygrlsdesign")}
      </div>
      <div className="panel p-6 space-y-4">
        <h3 className="title-xl text-2xl">Landing & Login hero images</h3>
        {field("Landing hero image URL", "landing_hero_image", 1)}
        {field("Login hero image URL", "login_hero_image", 1)}
      </div>
      <div className="panel p-6 space-y-4">
        <h3 className="title-xl text-2xl">Reminder cadence</h3>
        <p className="text-xs" style={{ color: "var(--text-dim)" }}>Informational — actual cron schedule requires redeploy of crons.yml.</p>
        {field("Task reminder frequency (days)", "task_reminder_frequency_days", 1, "How often open-task nudges are sent")}
        {field("Payment reminder — 1st (days before due)", "payment_reminder_frequency_days", 1, "First payment nudge cadence")}
        {field("Payment reminder — 2nd (days before due)", "payment_reminder_second_frequency_days", 1, "Second (closer) payment nudge cadence")}
      </div>
      <div className="panel p-6 space-y-4">
        <h3 className="title-xl text-2xl">Landing page copy</h3>
        {field("Headline", "landing_headline", 1)}
        {field("Tagline", "landing_tagline", 3)}
      </div>
      <div className="panel p-6 space-y-4">
        <h3 className="title-xl text-2xl">Login page copy</h3>
        {field("Headline", "login_headline", 1)}
        {field("Tagline", "login_tagline", 1)}
      </div>
      <div className="panel p-6 space-y-4">
        <h3 className="title-xl text-2xl">Sample edits — Jotform form</h3>
        {field("Jotform edit form URL (used when a sample's edits channel is set to \"Jotform\")", "web_design_edit_form_url", 1)}
      </div>
      <button data-testid="save-settings-btn" disabled={busy} onClick={save} className="btn-primary justify-center w-full h-12">
        <FloppyDisk size={14} weight="bold" /> {busy ? "Saving…" : "Save all settings"}
      </button>
    </div>
  );
}

function AdminsPanel() {
  const [admins, setAdmins] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const { user } = useAuth();
  const load = () => api.get("/admin/settings/admins").then(r => setAdmins(r.data));
  useEffect(() => { load(); }, []);
  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/settings/admins", form);
      toast.success("Admin created");
      setForm({ name: "", email: "", password: "" }); setShow(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  const del = async (a) => {
    if (!window.confirm(`Remove admin ${a.email}?`)) return;
    try {
      await api.delete(`/admin/settings/admins/${a.user_id}`);
      toast.success("Removed"); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  return (
    <div className="space-y-4" data-testid="admins-panel">
      <div className="flex justify-between items-baseline">
        <h3 className="title-xl text-2xl">Admin accounts</h3>
        <button data-testid="new-admin-btn" onClick={() => setShow(true)} className="btn-primary">
          <Plus size={14} weight="bold" /> New admin
        </button>
      </div>
      <div className="panel">
        <table className="w-full text-left">
          <thead style={{ background: "var(--invert-bg)", color: "var(--invert-text)" }}>
            <tr>
              <th className="label p-4">Name</th>
              <th className="label p-4">Email</th>
              <th className="label p-4">Provider</th>
              <th className="label p-4"></th>
            </tr>
          </thead>
          <tbody>
            {admins.map((a) => (
              <tr key={a.user_id} className="border-t" style={{ borderColor: "var(--hair)" }}
                  data-testid={`admin-row-${a.user_id}`}>
                <td className="p-4 font-medium">{a.name}</td>
                <td className="p-4">{a.email}</td>
                <td className="p-4 label">{a.auth_provider}</td>
                <td className="p-4">
                  {a.user_id !== user?.user_id && (
                    <button data-testid={`del-admin-${a.user_id}`} onClick={() => del(a)} className="btn-ghost text-xs">
                      <Trash size={12} weight="bold" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {show && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="panel w-full max-w-md p-8">
            <div className="flex justify-between items-start mb-4">
              <h3 className="title-xl text-2xl">New admin</h3>
              <button onClick={() => setShow(false)} className="btn-ghost p-2"><X size={16} /></button>
            </div>
            <form onSubmit={create} className="space-y-3">
              <input required placeholder="Full name" value={form.name}
                data-testid="new-admin-name" onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full px-4 py-3" />
              <input required type="email" placeholder="Email" value={form.email}
                data-testid="new-admin-email" onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full px-4 py-3" />
              <input required type="password" placeholder="Password (min 6 chars)" value={form.password}
                data-testid="new-admin-password" onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="w-full px-4 py-3" />
              <button data-testid="submit-new-admin" className="btn-primary w-full justify-center h-12">
                Create admin <ArrowRight size={14} weight="bold" />
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function AppearanceTab() {
  const { theme, setTheme } = useTheme();
  return (
    <div className="panel p-6" data-testid="appearance-tab">
      <h3 className="title-xl text-2xl mb-4">Appearance</h3>
      <p className="mb-4" style={{ color: "var(--text-dim)" }}>The theme applies to both admin and client portals globally for this browser session.</p>
      <div className="flex gap-3">
        <button data-testid="pick-dark"
          onClick={() => setTheme("dark")}
          className={`btn-ghost ${theme === "dark" ? "border-2" : ""}`}>
          <Moon size={14} weight="bold" /> Dark
        </button>
        <button data-testid="pick-light"
          onClick={() => setTheme("light")}
          className={`btn-ghost ${theme === "light" ? "border-2" : ""}`}>
          <Sun size={14} weight="bold" /> Light
        </button>
      </div>
    </div>
  );
}

export default function AdminSettings() {
  const [sp, setSp] = useSearchParams();
  const tab = sp.get("tab") || "emails";
  const setTab = (t) => setSp({ tab: t });
  const tabs = [
    { key: "emails", label: "Emails & links" },
    { key: "admins", label: "Admin accounts" },
    { key: "appearance", label: "Appearance" },
  ];
  return (
    <div className="min-h-screen" data-testid="admin-settings">
      <header className="flex items-center justify-between px-6 md:px-6 py-6 border-b" style={{ borderColor: "var(--hair)" }}>
        <div className="flex items-center gap-4">
          <NavButtons home="/admin" testid="settings-back" />
          <div>
            <p className="label">Console</p>
            <h1 className="title-xl text-3xl md:text-4xl mt-1">Settings</h1>
          </div>
        </div>
      </header>

      <div className="px-6 md:px-6 pt-6">
        <Tabs tabs={tabs} active={tab} onChange={setTab} testid="settings-tabs" />
      </div>

      <div className="p-6 md:p-6">
        {tab === "emails" && <EmailSettings />}
        {tab === "admins" && <AdminsPanel />}
        {tab === "appearance" && <AppearanceTab />}
      </div>
    </div>
  );
}
